import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, DollarSign, Percent, CheckCircle, AlertCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const BillingSettings = () => {
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [settings, setSettings] = useState({
    landing_fee_rate: 0,
    fuel_surcharge_percent: 0,
    tax_rate: 0
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE}/company/settings`);
      if (response.data) {
        setSettings({
          landing_fee_rate: response.data.landing_fee_rate || 0,
          fuel_surcharge_percent: response.data.fuel_surcharge_percent || 0,
          tax_rate: response.data.tax_rate || 0
        });
      }
    } catch (error) {
      console.error('Error fetching billing settings:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setSaveStatus(null);
    try {
      await axios.put(`${API_BASE}/company/settings`, settings);
      setSaveStatus({ type: 'success', message: 'Billing settings saved successfully!' });
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Error saving billing settings:', error);
      setSaveStatus({ type: 'error', message: 'Error saving billing settings' });
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-white">Billing & Fees Configuration</h3>
          <p className="text-gray-400 text-sm">Manage landing fees, fuel surcharges, and tax rates</p>
        </div>
        <button
          onClick={handleSave}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {saveStatus && (
        <div className={`mb-4 p-3 rounded-lg ${saveStatus.type === 'success' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
          {saveStatus.type === 'success' ? <CheckCircle className="w-4 h-4 inline mr-2" /> : <AlertCircle className="w-4 h-4 inline mr-2" />}
          {saveStatus.message}
        </div>
      )}

      <div className="space-y-6 max-w-md">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Landing Fee Rate ($)</label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="number"
              step="0.01"
              value={settings.landing_fee_rate}
              onChange={(e) => setSettings({ ...settings, landing_fee_rate: parseFloat(e.target.value) })}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Fee charged per landing</p>
        </div>

        <div>
          <label className="block text-sm text-gray-400 mb-1">Fuel Surcharge (%)</label>
          <div className="relative">
            <Percent className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="number"
              step="0.1"
              value={settings.fuel_surcharge_percent}
              onChange={(e) => setSettings({ ...settings, fuel_surcharge_percent: parseFloat(e.target.value) })}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm text-gray-400 mb-1">Tax Rate (%)</label>
          <div className="relative">
            <Percent className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="number"
              step="0.1"
              value={settings.tax_rate}
              onChange={(e) => setSettings({ ...settings, tax_rate: parseFloat(e.target.value) })}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingSettings;